/**
 * React Query hooks for accounting ingestion rules.
 *
 * Document batch upload/review/finalize now run on the shared ingestion
 * framework (`shared/ingestion` hooks). Only the accounting-owned ingestion
 * *rules* (condition→action defaults auto-applied by the accounting.bill
 * provider) keep their own hooks here.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ingestionApi, type IngestionRuleCreate } from '../services/accountingApi';

const RULES_KEY = 'ingestion-rules';

export function useIngestionRules(params?: { is_active?: boolean }) {
  return useQuery({
    queryKey: [RULES_KEY, params],
    queryFn: () => ingestionApi.listRules(params),
  });
}

export function useCreateIngestionRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: IngestionRuleCreate) => ingestionApi.createRule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [RULES_KEY] });
    },
  });
}

export function useUpdateIngestionRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      ruleId,
      data,
    }: {
      ruleId: string;
      data: Partial<IngestionRuleCreate> & { is_active?: boolean };
    }) => ingestionApi.updateRule(ruleId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [RULES_KEY] });
    },
  });
}

export function useDeleteIngestionRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ruleId: string) => ingestionApi.deleteRule(ruleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [RULES_KEY] });
    },
  });
}
